import { type ComponentType, type FC, useState } from "react";

import { ChevronDown, ChevronRight, Users } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { EventType, trackEvent } from "@/shared/utils/core";
import {
  LearnMoreSheet,
  LearnMoreHeader,
  LearnMoreTitle,
  LearnMoreContent,
  LearnMoreFooter,
  LearnMoreFAQ,
  LearnMoreFeedback,
  LearnMoreSupportButton,
} from "@/shared/components/ui/LearnMoreSheet";
import type { FaqItem } from "@/shared/components/ui/LearnMoreSheet";

interface NoteCardProps {
  content?: React.ReactNode;
  variant?: "plain" | "info-box";
  align?: "left" | "center";
  className?: string;
  onClick?: () => void;
  cardIcon?: ComponentType<{ className?: string }>;
  cardTitle?: string;
  cardSubtitle?: React.ReactNode;
  collapsible?: boolean;
  helpItems?: FaqItem[];
  helpFeedbackKey?: string;
  showRequestLink?: boolean;
  onRequestClick?: () => void;
}

export const NoteCard: FC<NoteCardProps> = ({
  content,
  variant,
  align,
  className,
  onClick,
  cardIcon: CardIcon,
  cardTitle,
  cardSubtitle,
  collapsible,
  helpItems,
  helpFeedbackKey,
  showRequestLink,
  onRequestClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const hasHelp = helpItems && helpFeedbackKey;

  if (variant === "info-box") {
    return (
      <>
        <div
          className={cn(
            "mt-8 flex items-start gap-3 rounded-lg border border-primary bg-background p-4",
            (collapsible || hasHelp) && "cursor-pointer",
            className,
          )}
          onClick={
            hasHelp
              ? () => setHelpOpen(true)
              : collapsible
                ? () => {
                    setIsExpanded((prev) => {
                      if (!prev && cardTitle) {
                        trackEvent(EventType.ONBOARDING_HELP_EXPANDED, { card_title: cardTitle });
                      }
                      return !prev;
                    });
                  }
                : undefined
          }
        >
          {CardIcon && (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
              <CardIcon className="h-4 w-4 text-secondary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            {cardTitle && (
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">{cardTitle}</p>
                {hasHelp ? (
                  <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" />
                ) : collapsible ? (
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-secondary transition-transform",
                      isExpanded && "rotate-180",
                    )}
                  />
                ) : null}
              </div>
            )}
            {cardSubtitle && (
              <div
                className={cn(
                  "mt-1 text-secondary",
                  collapsible && !hasHelp && !isExpanded && "line-clamp-2",
                )}
              >
                {cardSubtitle}
              </div>
            )}
          </div>
        </div>

        {hasHelp && (
          <LearnMoreSheet open={helpOpen} onOpenChange={setHelpOpen}>
            <LearnMoreHeader>
              <LearnMoreTitle>Learn more</LearnMoreTitle>
            </LearnMoreHeader>
            <LearnMoreContent>
              <LearnMoreFAQ items={helpItems} />
              {showRequestLink && (
                <div className="px-5 pb-5">
                  <button
                    type="button"
                    onClick={() => onRequestClick?.()}
                    className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-primary bg-white px-4 text-sm font-medium text-brand"
                  >
                    <Users className="h-4 w-4 shrink-0" />
                    Request it from someone else
                  </button>
                </div>
              )}
              <LearnMoreFeedback feedbackKey={helpFeedbackKey} />
            </LearnMoreContent>
            <LearnMoreFooter>
              <LearnMoreSupportButton />
            </LearnMoreFooter>
          </LearnMoreSheet>
        )}
      </>
    );
  }

  if (onClick) {
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "text-sm text-stone-900 hover:underline hover:underline-offset-4 disabled:text-stone-300 disabled:underline disabled:underline-offset-4",
            className,
          )}
        >
          {content}
        </button>
      </div>
    );
  }

  return (
    <div className={cn("text-sm text-secondary", align !== "left" && "text-center", className)}>
      {content}
    </div>
  );
};
